/** Read an already-connected, exact project source without prompting or scanning. */
import { getStage1VideoProcessingMode } from './stage1-video-policy';
import { getVideoSourcePath, isExplicitReferenceOnlyVideo } from './video-storage-policy';

function relativePath(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const path = value.trim().replace(/\\/g, '/');
    if (!path || path.startsWith('/') || /[\x00-\x1f:]/.test(path)) return null;
    const parts = path.split('/');
    if (parts.some(part => !part || part === '.' || part === '..')) return null;
    return parts.join('/');
}

/**
 * Old reference-only imports can become processable when the supported limit
 * increases. Read only their recorded project/file path under the tenant's
 * current sync root. Never use the playback resolver's root-wide filename
 * search: two projects may contain identically named inspection clips.
 * A missing permission/path or changed source returns null, leaving the saved
 * descriptor visibly deferred. No permission prompt or cloud call is made.
 */
export async function recoverLocalVideoSource(
    project: Record<string, any>, video: Record<string, any>,
): Promise<File | null> {
    if (!isExplicitReferenceOnlyVideo(video)
        || !getStage1VideoProcessingMode(Number(video.size)).processInline) return null;
    const projectPath = relativePath(project.intakeSourcePath || project.syncRelativePath || project.relativePath);
    const videoPath = relativePath(getVideoSourcePath(video));
    if (!projectPath || !videoPath) return null;
    try {
        const { SyncFolderService } = await import('./sync-folder.js');
        const root = await SyncFolderService.getHandle();
        if (!root || typeof root.queryPermission !== 'function'
            || await root.queryPermission({ mode: 'read' }) !== 'granted') return null;
        const stripRoot = (path: string) => path.startsWith(`${root.name}/`)
            ? path.slice(String(root.name).length + 1) : path;
        const scopedProject = stripRoot(projectPath);
        const scopedVideo = stripRoot(videoPath);
        if (!scopedProject) return null;
        const candidate = scopedVideo.startsWith(`${scopedProject}/`)
            ? scopedVideo : `${scopedProject}/${scopedVideo}`;
        const file = await SyncFolderService._getFileByRelativePath(root, candidate);
        if (!(file instanceof File) || file.size !== Number(video.size)) return null;
        const expectedName = String(video.originalName || video.name || '').trim().toLowerCase();
        if (!expectedName || String(file.name || '').toLowerCase() !== expectedName) return null;
        if (Number.isFinite(video.lastModified) && video.lastModified > 0
            && file.lastModified !== video.lastModified) return null;
        return file;
    } catch {
        return null;
    }
}
